import { normalizeChatBlocks } from '@/lib/chatbotAsk';
import { stripChatMarkdownTables } from '@/lib/chatAnswerCopy';

export const MEETING_PREP_BLOCK_IDS = [
  'meeting_prep_attendees',
  'meeting_prep_momentum',
  'meeting_prep_questions',
  'meeting_prep_next_steps',
];

const SECTION_PATTERNS = [
  ['account_snapshot', /account snapshot/i],
  ['recent_developments', /recent developments/i],
  ['attendee_approach', /attendee approach/i],
  ['pain_hypotheses', /pain hypotheses/i],
  ['competitive_intel', /competitive intel/i],
];

function stripHeadingDecor(line) {
  return String(line || '')
    .replace(/^#{1,6}\s+/, '')
    .replace(/[*_`]/g, '')
    .replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/gu, '')
    .trim();
}

function headingKind(line) {
  const stripped = stripHeadingDecor(line);
  if (!stripped) return null;
  for (const [kind, pattern] of SECTION_PATTERNS) {
    if (pattern.test(stripped)) return kind;
  }
  return null;
}

function isSectionHeadingLine(line) {
  return headingKind(line) != null;
}

export function splitMeetingPrepSections(markdown) {
  const lines = String(markdown || '').split('\n');
  const sections = [];
  let current = { kind: 'intro', lines: [] };

  const flush = () => {
    sections.push({ kind: current.kind, text: current.lines.join('\n') });
  };

  for (const line of lines) {
    if (isSectionHeadingLine(line)) {
      if (current.lines.some((l) => l.trim()) || current.kind !== 'intro') {
        flush();
      }
      current = { kind: headingKind(line), lines: [line] };
      continue;
    }
    current.lines.push(line);
  }
  flush();
  return sections;
}

function takeBlock(byId, used, id, parts) {
  const block = byId.get(id);
  if (!block || used.has(id)) return;
  used.add(id);
  parts.push({ type: 'block', block });
}

/**
 * Place meeting-prep tables next to the matching prose sections when headings
 * are present; otherwise append them after the brief (answer, then tables).
 */
export function interleaveMeetingPrepContent(markdown, blocks) {
  const list = (Array.isArray(blocks) ? blocks : []).filter(Boolean);
  if (!list.length) return [{ type: 'markdown', text: String(markdown || '') }];

  const byId = new Map(list.map((block) => [block.id, block]));
  const used = new Set();
  const parts = [];
  const sections = splitMeetingPrepSections(stripChatMarkdownTables(markdown));

  for (const section of sections) {
    if (section.kind === 'account_snapshot') {
      takeBlock(byId, used, 'meeting_prep_attendees', parts);
    }
    if (section.text.trim()) {
      parts.push({ type: 'markdown', text: section.text });
    }
    if (section.kind === 'intro') {
      takeBlock(byId, used, 'meeting_prep_attendees', parts);
    }
    if (section.kind === 'account_snapshot') {
      takeBlock(byId, used, 'meeting_prep_momentum', parts);
    }
    if (section.kind === 'attendee_approach') {
      takeBlock(byId, used, 'meeting_prep_questions', parts);
    }
    if (section.kind === 'competitive_intel') {
      takeBlock(byId, used, 'meeting_prep_next_steps', parts);
    }
  }

  for (const block of list) {
    if (!used.has(block.id)) {
      used.add(block.id);
      parts.push({ type: 'block', block });
    }
  }
  return parts;
}

export function hasMeetingPrepBlocks(blocks) {
  return (Array.isArray(blocks) ? blocks : []).some((block) => (
    MEETING_PREP_BLOCK_IDS.includes(block?.id)
  ));
}

export function buildMeetingPrepBotMessage(data) {
  const blocks = normalizeChatBlocks(data?.blocks);
  return {
    sender: 'bot',
    text: data?.answer || '',
    id: Date.now(),
    ...(blocks ? { blocks } : {}),
  };
}
